import { createFileRoute, Link } from "@tanstack/react-router";
import { Reveal } from "@/components/site/Reveal";
import { CtaPanel, PageHero } from "@/components/site/Sections";
import { blogOgImageUrl, blogPostSocialMeta, getBlogPost } from "@/lib/blog-posts";

const POST = getBlogPost("choosing-an-accounting-partner")!;
const URL = "https://finendra.com/blog/choosing-an-accounting-partner";
const TITLE = "How to Choose an Outsourced Accounting Partner";
const DESCRIPTION =
  "Learn how outsourced accounting compares to in-house hiring, what benefits to expect, and the 12-point checklist for choosing a partner.";

const benefits = [
  {
    title: "Lower total cost than an in-house hire",
    body: "A full-time in-house staff accountant costs roughly $65,000-$85,000 in salary plus payroll taxes, benefits, software seats, and recruiting. An outsourced pod covers the same scope from $960/month for a junior accountant, $1,440/month for an accountant, or $1,920/month for a senior accountant — 160 hours of cover each — and you pay only for the hours your volume actually needs.",
  },
  {
    title: "A team instead of a single point of failure",
    body: "Outsourcing gives you a bookkeeper, a reviewing accountant, and controller oversight on the same engagement. Nothing stalls during vacation, sick leave, or resignation — the biggest hidden risk of a one-person finance function.",
  },
  {
    title: "Capacity that flexes with your volume",
    body: "SaaS billing spikes at renewal season and eCommerce volume triples in Q4. An outsourced partner adds hours for those months and scales back afterwards, instead of forcing you to hire for your busiest week of the year.",
  },
  {
    title: "Faster, cleaner monthly closes",
    body: "Providers run a documented close checklist across many clients, so reconciliations, accruals, and reporting land on a predictable calendar — typically a 5-10 business day close instead of an open-ended one.",
  },
  {
    title: "Systems and automation expertise included",
    body: "The right partner already knows QuickBooks, Xero, NetSuite, Bill.com, Stripe, Shopify, and Amazon settlement files. You inherit working integrations rather than paying an employee to learn them on the job.",
  },
  {
    title: "Audit, tax, and investor readiness",
    body: "Clean, documented books shorten diligence. When a raise, a lender review, or a tax filing arrives, the reconciliations and support schedules already exist instead of being rebuilt under deadline.",
  },
];

const models = [
  {
    model: "In-house hire",
    cost: "$85k-$110k fully loaded",
    coverage: "One person, one skill level",
    scaling: "Slow — new headcount per increment",
    risk: "Single point of failure; no review layer",
    best: "Companies with complex, constant, on-site finance work",
  },
  {
    model: "Local CPA firm",
    cost: "$150-$350 / hour",
    coverage: "Tax and compliance heavy; light on daily ops",
    scaling: "Limited during tax season",
    risk: "Day-to-day bookkeeping often deprioritized",
    best: "Annual tax filings and attestation work",
  },
  {
    model: "Outsourced accounting partner",
    cost: "$960-$1,920 / month",
    coverage: "Bookkeeping, AP/AR, payroll, reporting, controller review",
    scaling: "Add or reduce hours monthly",
    risk: "Requires clear scope and communication cadence",
    best: "SaaS, eCommerce, and services firms scaling finance ops",
  },
];

const checklist = [
  {
    q: "Do they know your revenue model?",
    a: "SaaS needs deferred revenue, MRR/ARR schedules, and Stripe or Chargebee reconciliation. eCommerce needs Shopify and Amazon settlement mapping, COGS by SKU, inventory, and multi-channel sales tax. Ask for a client example in your exact model.",
  },
  {
    q: "Who actually does the work, and who reviews it?",
    a: "Get named roles: preparer, reviewer, and escalation contact. Every deliverable should be reviewed by someone more senior than the person who prepared it.",
  },
  {
    q: "What is the documented close calendar?",
    a: "A credible partner commits to a close date (for example, day 10) and a fixed reporting package. Vague timelines are the most common source of client frustration.",
  },
  {
    q: "Which systems do they support natively?",
    a: "Confirm your accounting ledger, AP tool, payroll provider, and commerce or billing platform are all in scope — not just the general ledger.",
  },
  {
    q: "How is pricing structured?",
    a: "Look for a monthly retainer or transparent hourly rate with a defined scope, not per-transaction pricing that penalizes growth. Ask what triggers a price change.",
  },
  {
    q: "What are the security and access controls?",
    a: "Least-privilege access to your systems, encrypted document transfer, signed NDAs, and named individuals — never shared logins or personal email attachments.",
  },
  {
    q: "How do they handle time-zone coverage?",
    a: "Clarify overlap with your working hours and response-time expectations for urgent items such as a payment run or a payroll correction.",
  },
  {
    q: "What is the onboarding plan?",
    a: "Expect a cleanup assessment, a systems audit, a documented process handover, and a parallel-run month. Onboarding should take weeks, not a single call.",
  },
  {
    q: "Can they show references at your stage?",
    a: "Ask for two clients of similar size and industry, and ask those references specifically about accuracy and responsiveness.",
  },
  {
    q: "What happens if quality slips?",
    a: "There should be a remediation path and a no-lock-in exit clause with reasonable notice. Long contracts are a red flag when quality is unproven.",
  },
  {
    q: "Do you keep ownership of your data?",
    a: "Your ledger, documents, and workpapers must live in accounts you own, so you can leave without a migration project.",
  },
  {
    q: "Who owns tax filings?",
    a: "Confirm exactly which filings the partner prepares, which they support, and which stay with your CPA — sales tax, 1099s, and income tax are often split.",
  },
];

const redFlags = [
  "No named reviewer or unclear ownership of deliverables",
  "Pricing quoted before any review of your transaction volume",
  "Shared logins or documents sent over unencrypted email",
  "No committed close date or standard reporting package",
  "Long lock-in contracts with no remediation clause",
  "No experience with your billing or commerce platform",
];

export const Route = createFileRoute("/blog/choosing-an-accounting-partner")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      ...blogPostSocialMeta(POST),
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESCRIPTION,
          mainEntityOfPage: URL,
          image: [blogOgImageUrl(POST.slug)],

          datePublished: "2026-08-02",
          dateModified: "2026-08-02",
          author: { "@type": "Organization", name: "Finendra" },
          publisher: { "@type": "Organization", name: "Finendra" },
          about: "Outsourced accounting services",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://finendra.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Choosing an outsourced accounting partner",
              item: URL,
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: checklist.slice(0, 6).map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
    ],
  }),
  component: GuidePage,
});

function GuidePage() {
  return (
    <>
      <PageHero
        eyebrow="Guide · 12 min read"
        title="How to choose an outsourced accounting partner"
        body="Outsourced accounting services now handle the day-to-day finance function for most scaling SaaS and eCommerce businesses. This guide covers how outsourcing compares to an in-house hire, the benefits that actually show up on your P&L, and a 12-point checklist for evaluating providers."
      />

      <article className="px-6 pb-8">
        <div className="mx-auto max-w-3xl">
          <Reveal className="rounded-3xl border border-brand-navy/5 bg-brand-surface p-8">
            <h2 className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              What's inside
            </h2>
            <ul className="mt-5 space-y-2 text-brand-navy/70">
              <li>· Outsourced vs in-house vs CPA firm — a side-by-side comparison</li>
              <li>· Six benefits of outsourced accounting services</li>
              <li>· What SaaS and eCommerce finance teams need specifically</li>
              <li>· A 12-point selection checklist and six red flags</li>
            </ul>
          </Reveal>
        </div>
      </article>

      <section className="px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
              Outsourced vs in-house accounting
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-brand-navy/70">
              The decision is rarely about accounting skill — it is about cost per hour of coverage,
              continuity, and how quickly capacity can change. Most growing businesses need 40-120
              hours of finance work a month, which is awkward: too much for a founder, not enough
              for a strong full-time hire.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="mx-auto mt-10 max-w-5xl overflow-x-auto">
          <table className="w-full min-w-[820px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-brand-navy/10 text-xs uppercase tracking-widest text-brand-navy/50">
                <th className="py-4 pr-6 font-bold">Model</th>
                <th className="py-4 pr-6 font-bold">Typical cost</th>
                <th className="py-4 pr-6 font-bold">Coverage</th>
                <th className="py-4 pr-6 font-bold">Scaling</th>
                <th className="py-4 pr-6 font-bold">Main risk</th>
                <th className="py-4 font-bold">Best for</th>
              </tr>
            </thead>
            <tbody>
              {models.map((m) => (
                <tr key={m.model} className="border-b border-brand-navy/5 align-top">
                  <td className="py-5 pr-6 font-bold text-brand-navy">{m.model}</td>
                  <td className="py-5 pr-6 text-brand-navy/70">{m.cost}</td>
                  <td className="py-5 pr-6 text-brand-navy/70">{m.coverage}</td>
                  <td className="py-5 pr-6 text-brand-navy/70">{m.scaling}</td>
                  <td className="py-5 pr-6 text-brand-navy/70">{m.risk}</td>
                  <td className="py-5 text-brand-navy/70">{m.best}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>
      </section>

      <section className="bg-brand-surface px-6 py-24">
        <div className="mx-auto max-w-5xl">
          <Reveal>
            <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
              Top benefits of outsourced accounting services
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-8 md:grid-cols-2">
            {benefits.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.05} className="rounded-3xl bg-background p-8">
                <h3 className="font-display text-xl font-bold text-brand-navy">{b.title}</h3>
                <p className="mt-4 leading-relaxed text-brand-navy/70">{b.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-24">
        <div className="mx-auto max-w-3xl space-y-12">
          <Reveal>
            <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
              What SaaS companies should look for
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-brand-navy/70">
              Subscription businesses live or die on revenue recognition. Your partner should
              maintain deferred revenue schedules, reconcile Stripe or Chargebee payouts to the
              ledger (gross revenue, fees, refunds, and chargebacks separately), track MRR, ARR,
              churn, and net revenue retention alongside the P&L, and classify R&D versus hosting
              costs so gross margin means something to investors. Ask how they handle annual prepaid
              contracts, mid-term upgrades, and multi-entity consolidation before you sign.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
              What eCommerce brands should look for
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-brand-navy/70">
              For eCommerce, accuracy lives in the settlement files. A capable partner maps Shopify,
              Amazon, and marketplace payouts line by line — sales, shipping income, platform fees,
              refunds, and reserves — rather than booking the net deposit as revenue. They should
              track inventory and COGS by SKU, handle landed-cost allocation for freight and duties,
              monitor economic-nexus sales tax across states, and reconcile 3PL and merchant
              processor balances every month.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-navy px-6 py-24">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <h2 className="font-display text-3xl font-bold text-background md:text-4xl">
              The 12-point selection checklist
            </h2>
            <p className="mt-5 text-lg text-background/60">
              Run every shortlisted provider through the same questions and compare the answers side
              by side.
            </p>
          </Reveal>
          <ol className="mt-12 space-y-6">
            {checklist.map((item, i) => (
              <Reveal
                key={item.q}
                delay={Math.min(i * 0.04, 0.3)}
                className="flex gap-5 rounded-3xl bg-background/5 p-7"
              >
                <span className="font-display text-2xl font-bold text-brand-accent">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="font-display text-lg font-bold text-background">{item.q}</h3>
                  <p className="mt-3 leading-relaxed text-background/60">{item.a}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="px-6 py-24">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
              Six red flags
            </h2>
          </Reveal>
          <ul className="mt-10 space-y-4">
            {redFlags.map((flag, i) => (
              <Reveal
                key={flag}
                delay={i * 0.05}
                className="rounded-2xl border border-brand-navy/10 px-6 py-5 text-brand-navy/70"
              >
                {flag}
              </Reveal>
            ))}
          </ul>
          <Reveal delay={0.1} className="mt-12 text-lg leading-relaxed text-brand-navy/70">
            <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
              Making the decision
            </h2>
            <p className="mt-5">
              Estimate your monthly finance hours, price each model against that number, and weigh
              continuity and review quality alongside cost. If you need under roughly 120 hours a
              month, outsourced accounting services almost always deliver more coverage per dollar
              than a single in-house hire — with a reviewer built in.
            </p>
            <p className="mt-5">
              You can size your own engagement with the{" "}
              <Link to="/pricing" className="font-bold text-brand-accent underline">
                Finendra pricing calculator
              </Link>
              , review{" "}
              <Link to="/services" className="font-bold text-brand-accent underline">
                what our finance pods cover
              </Link>
              , or see how we work with{" "}
              <Link to="/industries" className="font-bold text-brand-accent underline">
                SaaS and eCommerce teams
              </Link>
              .
            </p>
          </Reveal>
        </div>
      </section>

      <CtaPanel
        title="Ready to compare us against your checklist?"
        body="Bring your questions — we'll walk through scope, close calendar, systems, and pricing on a single call."
      />
    </>
  );
}
